/**
 * StateDetailView - Detail view for drilling into specific state data
 *
 * Features:
 * - County breakdown
 * - City breakdown
 * - Carrier concentration
 * - Inspection stations
 * - Safety metrics for state
 * - Timeline trends
 */

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  MapPin,
  Building2,
  TrendingUp,
  AlertTriangle,
  Truck,
  Shield,
  Activity,
  Users,
} from 'lucide-react';
import { useDrillDown } from '../../contexts/DrillDownContext';
import type { Violation, Crash, Inspection, CarrierProfile } from '../../types/api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface CountyData {
  county: string;
  fips?: string;
  violations: number;
  inspections: number;
  crashes: number;
}

interface CityData {
  city: string;
  county?: string;
  violations: number;
  inspections: number;
  crashes: number;
}

interface CategoryChartData {
  category: string;
  count: number;
  percentage?: number;
}

interface StateDetailViewProps {
  data: {
    state?: string;
    violations?: Violation[];
    crashes?: Crash[];
    inspections?: Inspection[];
    carriers?: CarrierProfile[];
    counties?: CountyData[];
    cities?: CityData[];
    category?: string;
    month?: string;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

import { CHART_COLORS } from '../../constants/colors';

const STATE_NAMES: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming',
};

// ============================================================================
// COMPONENT
// ============================================================================

const StateDetailView: React.FC<StateDetailViewProps> = ({ data }) => {
  const { drillDown } = useDrillDown();

  const stateCode = data?.state || 'Unknown';
  const stateName = STATE_NAMES[stateCode] || stateCode;
  const violations = data?.violations || [];
  const crashes = data?.crashes || [];
  const inspections = data?.inspections || [];
  const carriers = data?.carriers || [];

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalViolations = violations.length;
    const totalCrashes = crashes.length;
    const totalInspections = inspections.length;
    const totalCarriers = carriers.length;
    const oosViolations = violations.filter(v => v.outOfServiceIndicator === 'Y').length;
    const fatalities = crashes.reduce((sum, c) => sum + (c.fatalities || 0), 0);
    const injuries = crashes.reduce((sum, c) => sum + (c.injuries || 0), 0);

    return {
      totalViolations,
      totalCrashes,
      totalInspections,
      totalCarriers,
      oosViolations,
      oosRate: totalViolations > 0 ? ((oosViolations / totalViolations) * 100).toFixed(1) : '0',
      fatalities,
      injuries,
      violationRate: totalInspections > 0
        ? ((totalViolations / totalInspections) * 100).toFixed(1)
        : '0',
    };
  }, [violations, crashes, inspections, carriers]);

  // County breakdown
  const countyBreakdown = useMemo(() => {
    const countyMap = new Map<string, { violations: number; inspections: number; crashes: number }>();

    violations.forEach(v => {
      const county = v.county_code || v.county || 'Unknown';
      if (!countyMap.has(county)) {
        countyMap.set(county, { violations: 0, inspections: 0, crashes: 0 });
      }
      countyMap.get(county)!.violations++;
    });

    inspections.forEach(i => {
      const county = i.county_code || i.county || 'Unknown';
      if (!countyMap.has(county)) {
        countyMap.set(county, { violations: 0, inspections: 0, crashes: 0 });
      }
      countyMap.get(county)!.inspections++;
    });

    crashes.forEach(c => {
      const county = c.county_code || c.county || 'Unknown';
      if (!countyMap.has(county)) {
        countyMap.set(county, { violations: 0, inspections: 0, crashes: 0 });
      }
      countyMap.get(county)!.crashes++;
    });

    return Array.from(countyMap.entries())
      .map(([county, stats]) => ({ county, ...stats }))
      .sort((a, b) => b.violations - a.violations)
      .slice(0, 15);
  }, [violations, inspections, crashes]);

  // City breakdown
  const cityBreakdown = useMemo(() => {
    const cityMap = new Map<string, { violations: number; inspections: number }>();

    violations.forEach(v => {
      const city = v.city || v.location_desc || 'Unknown';
      if (!cityMap.has(city)) {
        cityMap.set(city, { violations: 0, inspections: 0 });
      }
      cityMap.get(city)!.violations++;
    });

    inspections.forEach(i => {
      const city = i.city || i.location_desc || 'Unknown';
      if (!cityMap.has(city)) {
        cityMap.set(city, { violations: 0, inspections: 0 });
      }
      cityMap.get(city)!.inspections++;
    });

    return Array.from(cityMap.entries())
      .map(([city, stats]) => ({ city, ...stats }))
      .sort((a, b) => b.violations - a.violations)
      .slice(0, 10);
  }, [violations, inspections]);

  // BASIC category distribution
  const basicDistribution = useMemo(() => {
    const categoryMap = new Map<string, number>();

    violations.forEach(v => {
      const category = v.basicCategory || 'Unknown';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    return Array.from(categoryMap.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);
  }, [violations]);

  // Timeline trend
  const timelineTrend = useMemo(() => {
    const monthMap = new Map<string, { violations: number; inspections: number }>();

    violations.forEach(v => {
      const dateStr = v.inspDate || v.inspectionDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthMap.has(key)) {
          monthMap.set(key, { violations: 0, inspections: 0 });
        }
        monthMap.get(key)!.violations++;
      }
    });

    inspections.forEach(i => {
      const dateStr = i.inspDate || i.inspectionDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthMap.has(key)) {
          monthMap.set(key, { violations: 0, inspections: 0 });
        }
        monthMap.get(key)!.inspections++;
      }
    });

    return Array.from(monthMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([month, stats]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        ...stats,
      }));
  }, [violations, inspections]);

  // Top carriers
  const topCarriers = useMemo(() => {
    const carrierMap = new Map<string, { name: string; violations: number; inspections: number }>();

    violations.forEach(v => {
      const dot = String(v.dotNumber || v.carrier_dot || 'Unknown');
      const name = v.carrier_name || v.legalName || dot;
      if (!carrierMap.has(dot)) {
        carrierMap.set(dot, { name: String(name), violations: 0, inspections: 0 });
      }
      carrierMap.get(dot)!.violations++;
    });

    inspections.forEach(i => {
      const dot = String(i.dotNumber || i.carrier_dot || 'Unknown');
      const name = i.carrier_name || i.legalName || dot;
      if (!carrierMap.has(dot)) {
        carrierMap.set(dot, { name: String(name), violations: 0, inspections: 0 });
      }
      carrierMap.get(dot)!.inspections++;
    });

    return Array.from(carrierMap.entries())
      .map(([dot, stats]) => ({ dot, ...stats }))
      .sort((a, b) => b.violations - a.violations)
      .slice(0, 10);
  }, [violations, inspections]);

  // Handle county click
  const handleCountyClick = (county: string) => {
    const countyViolations = violations.filter(v => (v.county_code || v.county) === county);
    const countyInspections = inspections.filter(i => (i.county_code || i.county) === county);
    drillDown('county', `${county} County`, {
      county,
      state: stateCode,
      violations: countyViolations,
      inspections: countyInspections,
    });
  };

  // Handle category click
  const handleCategoryClick = (category: string) => {
    const categoryViolations = violations.filter(v => v.basicCategory === category);
    drillDown('violation-category', category, {
      category,
      violations: categoryViolations,
      state: stateCode,
    });
  };

  // Handle carrier click
  const handleCarrierClick = (dot: string, name: string) => {
    const carrierViolations = violations.filter(v => String(v.dotNumber || v.carrier_dot) === dot);
    const carrierInspections = inspections.filter(i => String(i.dotNumber || i.carrier_dot) === dot);
    drillDown('carrier-detail', name || dot, {
      dot,
      name,
      violations: carrierViolations,
      inspections: carrierInspections,
      state: stateCode,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* State Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderLeft: '4px solid #10b981',
        borderRadius: '8px',
      }}>
        <MapPin size={28} style={{ color: '#10b981' }} />
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>
            {stateName} ({stateCode})
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>
            {data?.category ? `Category: ${data.category}` : ''}
            {data?.month ? ` | Month: ${data.month}` : ''}
            {!data?.category && !data?.month ? 'Geographic Analysis' : ''}
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="drill-down-stats-row">
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.totalInspections.toLocaleString()}</div>
          <div className="drill-down-stat-label">Inspections</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-warning)' }}>
            {summaryStats.totalViolations.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">Violations</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
            {summaryStats.totalCrashes.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">Crashes</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.oosRate}%</div>
          <div className="drill-down-stat-label">OOS Rate</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.violationRate}%</div>
          <div className="drill-down-stat-label">Violation Rate</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="drill-down-detail-grid">
        {/* County Breakdown */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <MapPin size={18} style={{ color: 'var(--color-primary)' }} />
              County Breakdown
            </h4>
            <span className="drill-down-detail-card-badge">
              Top 15
            </span>
          </div>
          {countyBreakdown.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={countyBreakdown} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis
                    type="category"
                    dataKey="county"
                    tick={{ fontSize: 9 }}
                    width={75}
                  />
                  <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar
                    dataKey="violations"
                    fill="#f59e0b"
                    name="Violations"
                    onClick={(data: any) => handleCountyClick(data.county)}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="inspections"
                    fill="#3b82f6"
                    name="Inspections"
                    onClick={(data: any) => handleCountyClick(data.county)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No county data available
            </div>
          )}
        </div>

        {/* City Breakdown */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Building2 size={18} style={{ color: 'var(--color-info)' }} />
              Top Cities
            </h4>
          </div>
          {cityBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={cityBreakdown} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis
                  type="category"
                  dataKey="city"
                  tick={{ fontSize: 9 }}
                  width={75}
                  tickFormatter={(value) => value.length > 12 ? value.substring(0, 10) + '...' : value}
                />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Bar dataKey="violations" fill="#8b5cf6" name="Violations" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No city data available
            </div>
          )}
        </div>

        {/* BASIC Categories */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Shield size={18} style={{ color: 'var(--color-warning)' }} />
              Violation Categories
            </h4>
          </div>
          {basicDistribution.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={basicDistribution}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }: any) => `${((percent as number) * 100).toFixed(0)}%`}
                    onClick={(data: CategoryChartData) => handleCategoryClick(data.category)}
                    cursor="pointer"
                  >
                    {basicDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ fontSize: '0.8125rem' }}
                    formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No category data available
            </div>
          )}
        </div>

        {/* Timeline Trend */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <TrendingUp size={18} style={{ color: 'var(--color-success)' }} />
              Activity Trend
            </h4>
            <span className="drill-down-detail-card-badge">
              12 Months
            </span>
          </div>
          {timelineTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={timelineTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Line
                  type="monotone"
                  dataKey="violations"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="Violations"
                />
                <Line
                  type="monotone"
                  dataKey="inspections"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Inspections"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No timeline data available
            </div>
          )}
        </div>
      </div>

      {/* Top Carriers */}
      {topCarriers.length > 0 && (
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Truck size={18} style={{ color: 'var(--color-primary)' }} />
              Top Carriers in {stateCode}
            </h4>
            <span className="drill-down-detail-card-badge">
              {topCarriers.length} carriers
            </span>
          </div>
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: 'white' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>DOT Number</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>Violations</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>Inspections</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {topCarriers.map((carrier, idx) => (
                  <tr
                    key={idx}
                    style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                    onClick={() => handleCarrierClick(carrier.dot, carrier.name)}
                    className="chart-clickable"
                  >
                    <td style={{ padding: '0.75rem', fontFamily: 'monospace' }}>{carrier.dot}</td>
                    <td style={{ padding: '0.75rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {carrier.name}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--color-warning)' }}>
                      {carrier.violations.toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {carrier.inspections.toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {carrier.inspections > 0
                        ? ((carrier.violations / carrier.inspections) * 100).toFixed(1)
                        : '0'}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Crash Summary */}
      {summaryStats.totalCrashes > 0 && (
        <div className="drill-down-detail-card" style={{ backgroundColor: 'rgba(239, 68, 68, 0.03)' }}>
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <AlertTriangle size={18} style={{ color: 'var(--color-danger)' }} />
              Crash Summary
            </h4>
          </div>
          <div className="drill-down-stats-row">
            <div className="drill-down-stat-item">
              <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
                {summaryStats.totalCrashes.toLocaleString()}
              </div>
              <div className="drill-down-stat-label">Total Crashes</div>
            </div>
            <div className="drill-down-stat-item">
              <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
                {summaryStats.fatalities}
              </div>
              <div className="drill-down-stat-label">Fatalities</div>
            </div>
            <div className="drill-down-stat-item">
              <div className="drill-down-stat-value" style={{ color: 'var(--color-warning)' }}>
                {summaryStats.injuries}
              </div>
              <div className="drill-down-stat-label">Injuries</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateDetailView;
